import { registrableDomain, canonCompanyUrl } from '../keys.js';
import { NOT_FOUND_TRUST_DAYS } from './provenance.js';
/** One person at one company. Separate from identity so a serial founder works. */
/**
 * A company as the Companies screen shows it.
 *
 * The COMPANY is the spine here, one row per registrable domain, the same way
 * the contacts screen is one row per person. A company that appears in four
 * uploads is one row that has been profiled four times, not four rows — which is
 * the question that screen exists to answer: do we already know this company,
 * and what did we learn last time.
 *
 * Everything on it is already stored on the company record. Nothing is counted
 * by joining the employment table, because that would be a query per row for a
 * number the ladder already wrote down.
 */
/**
 * The Companies screen's repair filter. Answered from the repair state mirrored
 * onto the company record, so it costs the same as every other filter.
 *
 * `retired` is the dead ones; `corrected`, `renamed` and `unchanged` are the
 * verdicts applied; `queued` waits for a person; `failed` could not be examined
 * yet; `examined` is any of those; `none` was never examined.
 */
export const COMPANY_REPAIR_FILTERS = [
    'retired',
    'corrected',
    'renamed',
    'unchanged',
    'queued',
    'failed',
    'examined',
    'none',
];
/**
 * The Companies screen asks four separate questions - is the website up, is a
 * LinkedIn page known, were people found, where did repair leave it - and each
 * gets its own filter so they combine. People and LinkedIn used to share one
 * "Outcome" control, so "live site, has a page, nobody found" could not be asked.
 */
export const COMPANY_PEOPLE_FILTERS = ['found', 'none_found', 'never_searched'];
export const COMPANY_LINKEDIN_FILTERS = ['has', 'none'];
/** `zoho`: the company is an Account in Zoho CRM, imported from it or pushed to it. `none`: it is not. */
export const COMPANY_CRM_FILTERS = ['zoho', 'none'];
/** The old single "Outcome" value, as the two filters it was really choosing between. */
export function splitCompanyOutcome(outcome) {
    switch (outcome) {
        case 'with_people':
            return { people: 'found', linkedin: null };
        case 'no_people':
            return { people: 'none_found', linkedin: null };
        case 'never_searched':
            return { people: 'never_searched', linkedin: null };
        case 'has_linkedin':
            return { people: null, linkedin: 'has' };
        case 'no_linkedin':
            return { people: null, linkedin: 'none' };
        default:
            return { people: null, linkedin: null };
    }
}
/** The combination a search is recognised by. Canonical, so the same search always has the same key. */
export function companySearchKey(search) {
    const domain = registrableDomain(search.domain) ?? (search.domain ? search.domain.trim().toLowerCase() : '-');
    const page = canonCompanyUrl(search.linkedinUrl) ?? '-';
    return [search.provider, domain, page, search.presetId].join('|');
}
/**
 * The identical search that already found nobody here, recently enough to believe.
 *
 * Returns the record, so the caller can say what it skipped and when. A search
 * that found people is not a reason to skip - the reuse cache replays those - and
 * a miss older than NOT_FOUND_TRUST_DAYS is worth asking again.
 */
export function repeatOfFruitlessSearch(log, search, now = new Date()) {
    const record = log?.[companySearchKey(search)];
    if (record?.outcome !== 'nobody')
        return null;
    const ageMs = now.getTime() - Date.parse(record.at);
    if (!Number.isFinite(ageMs))
        return null;
    return ageMs < NOT_FOUND_TRUST_DAYS * 24 * 60 * 60 * 1000 ? record : null;
}
/**
 * What the mail actually did.
 *
 * `sent` means it left and nothing came back, which is weak evidence and is
 * counted as such. `bounced` and `left_company` are the two that falsify a gate
 * — the first says the address was wrong, the second says the person was. A
 * `replied` is the only outcome that proves the whole chain was right.
 */
export const DELIVERY_OUTCOMES = ['sent', 'bounced', 'replied', 'left_company', 'unsubscribed'];
/** The outcomes that say a gate let through something it should not have. */
export const FALSIFYING_OUTCOMES = ['bounced', 'left_company'];
/**
 * How many mailed contacts a facet needs before a rate is shown for it.
 *
 * Small enough to say something after one campaign, large enough that a single
 * bad address cannot make a rule look broken. Below it the counts are still
 * reported — they are facts — and only the RATE is withheld.
 */
export const MIN_OUTCOMES_TO_JUDGE = 20;
//# sourceMappingURL=contacts.js.map